import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import { getArticleById, getPopularArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const popularArticles = useMemo(() => getPopularArticles(4), []);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <p className="text-foreground-600">文章未找到</p>
      </div>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === article.category);
  const relatedArticles = popularArticles.filter((a) => a.id !== article.id).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: 'asmrone',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://asmrone.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryInfo?.name || '分类',
        item: `https://asmrone.com/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `https://asmrone.com/article/${article.id}`,
      },
    ],
  };

  return (
    <>
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={`asmrone,${article.tags.join(',')},ASMR音频`}
        canonicalUrl={`https://asmrone.com/article/${article.id}`}
        ogImage={article.image}
        ogType="article"
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <main className="min-h-screen pt-16">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors">
              {categoryInfo?.name || '分类'}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium truncate max-w-[200px]">{article.title}</span>
          </nav>

          <article>
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {categoryInfo && (
                  <Link
                    to={`/category/${article.category}`}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors"
                  >
                    {categoryInfo.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-500">{article.duration}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-headphone-line"></i>
                  {article.listenCount.toLocaleString()} 次收听
                </span>
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600 text-sm"></i>
                </div>
                <span className="text-sm font-medium text-foreground-700">{article.author}</span>
              </div>
            </header>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-base max-w-none text-foreground-800 leading-relaxed mb-10"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center gap-2 flex-wrap mb-12 pt-6 border-t border-background-200/60">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </article>

          <div className="border-t border-background-200/60 pt-10">
            <h3 className="text-xl font-heading font-bold text-foreground-900 mb-6">
              相关推荐
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group rounded-lg bg-background-100/70 border border-background-200/60 hover:border-primary-200/50 transition-all overflow-hidden"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={related.image}
                      alt={related.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="text-sm font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {related.title}
                    </h4>
                    <p className="text-xs text-foreground-500 mt-2">{related.duration} · {related.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}